import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@shared/lib/utils';
import {
  LayoutDashboard,
  Users,
  FileText,
  AlertTriangle,
  Building2,
  Settings,
  Briefcase,
  QrCode,
  UserCheck,
  Bell,
  MapPin,
  ShieldCheck,
  ShieldAlert,
  Crown,
  Inbox,
  Sun,
  Moon,
  LogOut,
} from 'lucide-react';
import { isOwner } from '@shared/utils/permissions';
import { useTheme } from '@shared/hooks/useTheme';

const APP_VERSION = '1.1.0';

const Sidebar = ({
  onLogout,
  user,
  openIncidentsCount = 0,
  currentOccupancy = 0,
  pendingProjectsCount = 0,
  pendingInvitesCount = 0,
  pendingExtensionReviews = 0,
  myOpenActionsCount = 0,
  onClose,
}) => {
  const { theme, toggleTheme } = useTheme();

  const NavItem = ({ to, icon: Icon, label, badge, end: endProp }) => (
    <NavLink
      to={to}
      end={endProp}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        )
      }
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      {label}
      {badge > 0 && (
        <span className="ml-auto flex items-center justify-center min-w-[22px] h-[22px] text-xs font-bold rounded-full bg-sky-500 text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );

  const SectionHeading = ({ label, pt = false }) => (
    <div className={cn('pb-1 px-3 flex flex-col items-center gap-2', pt && 'pt-4')}>
      <div className="h-px w-full bg-primary/40" />
      <p className="text-xs font-semibold text-primary uppercase tracking-widest">{label}</p>
      <div className="h-px w-full bg-primary/40" />
    </div>
  );

  const can = (roles) => user?.role === 'admin' || roles.includes(user?.role);

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="relative flex flex-col items-center justify-center px-4 pt-4 pb-3 border-b border-border">
        <img
          src="/assets/Atlaslogo.png"
          alt="Atlas Logo"
          className="w-full h-auto max-h-32 object-contain"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">

        <SectionHeading label="Main" />
        {can(['admin', 'depot_manager', 'approver', 'employee']) && (
          <NavItem to="/app/home" icon={LayoutDashboard} label="Home" badge={currentOccupancy} end />
        )}
        {can(['admin', 'depot_manager', 'approver']) && (
          <NavItem to="/app/dashboard" icon={FileText} label="Dashboard" />
        )}
        {can(['admin', 'depot_manager', 'approver', 'employee']) && (
          <NavItem to="/app/roll-call" icon={UserCheck} label="Emergency Roll Call" />
        )}
        {can(['admin', 'depot_manager', 'kiosk', 'employee']) && (
          <NavItem to="/app/kiosk" icon={QrCode} label="Sign In / Out" />
        )}
        {can(['admin', 'depot_manager', 'approver', 'employee']) && (
          <NavItem to="/app/incidents" icon={AlertTriangle} label="Incident Reporting" badge={openIncidentsCount} />
        )}

        {can(['admin', 'depot_manager']) && (
          <>
            <SectionHeading label="Site Management" pt />
            <NavItem to="/app/visitors" icon={Users} label="Site Invites" badge={pendingInvitesCount} />
            {can(['admin', 'depot_manager', 'approver', 'employee']) && (
              <NavItem to="/app/projects" icon={Briefcase} label="Site Projects" badge={pendingProjectsCount} />
            )}
          </>
        )}

        {can(['admin', 'depot_manager']) && (
          <>
            <SectionHeading label="Admin" pt />
            <NavItem to="/app/induction-briefs" icon={FileText} label="Induction Briefs" />
            <NavItem to="/app/qr-codes" icon={QrCode} label="QR Pass Generator" />
            <NavItem to="/app/user-management" icon={Users} label="Users" />
            <NavItem to="/app/reports" icon={FileText} label="Reports" />
          </>
        )}

        {can(['admin', 'depot_manager', 'approver', 'employee']) && (
          <>
            <SectionHeading label="Auditing" pt />
            {can(['admin', 'depot_manager', 'approver']) && (
              <NavItem to="/app/audit-hub" icon={ShieldAlert} label="Audit Hub" badge={pendingExtensionReviews} />
            )}
            <NavItem to="/app/my-actions" icon={Inbox} label="My Actions" badge={myOpenActionsCount} />
          </>
        )}

        {can(['admin', 'depot_manager', 'approver', 'employee']) && (
          <>
            <SectionHeading label="Settings" pt />
            {can(['admin']) && (
              <NavItem to="/app/depots" icon={Building2} label="Depots" />
            )}
            {can(['admin', 'depot_manager']) && (
              <NavItem to="/app/locations" icon={MapPin} label="Work Areas" />
            )}
            {can(['admin', 'depot_manager']) && (
              <NavItem to="/app/notifications" icon={Bell} label="Notifications" />
            )}
            {can(['admin', 'depot_manager']) && (
              <NavItem to="/app/system-settings" icon={Settings} label="System" />
            )}
            {can(['admin', 'depot_manager', 'approver', 'employee']) && (
              <NavItem to="/app/account-security" icon={ShieldCheck} label="Security" />
            )}
            {isOwner(user) && (
              <NavItem to="/app/subscription" icon={Crown} label="Subscription" />
            )}
          </>
        )}

        {user?.role === 'kiosk' && (
          <a
            href="/kiosk"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-primary text-black hover:bg-primary/90 transition-colors mt-2"
          >
            <QrCode className="w-4 h-4" />
            Open Kiosk Mode
          </a>
        )}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-border space-y-1">
        <button
          onClick={toggleTheme}
          className="flex items-center justify-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button
          onClick={onLogout}
          className="flex items-center justify-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
        <div className="px-3 pt-2 text-center">
          <p className="text-sm font-medium text-foreground truncate">
            {user?.first_name
              ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
              : user?.username}
          </p>
          <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          <p className="text-xs text-primary/80 mt-1">v{APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

